import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import CrmLayout from "../crmLayout/CrmLayout";
import usePageTitle from "../utils/usePageTitle";

// Culori pe rol: text + culoare, niciodată doar culoare (regulă UX din plan).
const ROL_BADGE = {
  ADMIN: "bg-primary-50 text-primary-600",
  COORDONATOR: "bg-warning-50 text-warning-600",
  PROGRAMATOR: "bg-success-50 text-success-600",
  SEO: "bg-info-50 text-info-600",
};

const ROL_LABEL = {
  ADMIN: "Admin",
  COORDONATOR: "Coordonator",
  PROGRAMATOR: "Programator",
  SEO: "SEO",
};

function fmtData(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function ConfirmDialog({ titlu, text, onDa, onNu }) {
  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.35)" }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content radius-16 border bg-base">
          <div className="modal-body p-24">
            <h5 className="mb-8">{titlu}</h5>
            <p className="text-secondary-light mb-24">{text}</p>
            <div className="d-flex justify-content-end gap-8">
              <button type="button" className="btn btn-outline-secondary radius-8" onClick={onNu}>
                Anulează
              </button>
              <button type="button" className="btn btn-danger radius-8" onClick={onDa}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalAngajat({ editare, roluri, onSalveaza, onInchide, eroare, salveaza }) {
  const [form, setForm] = useState({
    nume: editare?.nume || "",
    email: editare?.email || "",
    password: "",
    roleId: editare?.roleId || roluri[0]?.id || "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    onSalveaza(form);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.35)" }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content radius-16 border bg-base">
          <form onSubmit={submit}>
            <div className="modal-header p-24 pb-12">
              <h5 className="modal-title">{editare ? "Editează angajatul" : "Adaugă angajat"}</h5>
              <button type="button" className="btn-close" onClick={onInchide} aria-label="Închide" />
            </div>
            <div className="modal-body p-24 pt-12">
              {eroare && <div className="alert alert-danger py-8">{eroare}</div>}
              <div className="mb-16">
                <label className="form-label fw-semibold text-secondary-light" htmlFor="ang-nume">Nume complet</label>
                <input id="ang-nume" type="text" className="form-control radius-8" value={form.nume}
                  onChange={set("nume")} required />
              </div>
              <div className="mb-16">
                <label className="form-label fw-semibold text-secondary-light" htmlFor="ang-email">Email</label>
                <input id="ang-email" type="email" className="form-control radius-8" value={form.email}
                  onChange={set("email")} required />
              </div>
              {!editare && (
                <div className="mb-16">
                  <label className="form-label fw-semibold text-secondary-light" htmlFor="ang-parola">Parolă (minim 8 caractere)</label>
                  <input id="ang-parola" type="password" className="form-control radius-8" value={form.password}
                    onChange={set("password")} required minLength={8} />
                </div>
              )}
              <div>
                <label className="form-label fw-semibold text-secondary-light" htmlFor="ang-rol">Rol</label>
                <select id="ang-rol" className="form-select radius-8" value={form.roleId} onChange={set("roleId")} required>
                  {roluri.map((r) => (
                    <option key={r.id} value={r.id}>{ROL_LABEL[r.nume] || r.nume}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer p-24 pt-0">
              <button type="button" className="btn btn-outline-secondary radius-8" onClick={onInchide}>
                Anulează
              </button>
              <button type="submit" className="btn btn-primary radius-8" disabled={salveaza}>
                {salveaza ? "Se salvează…" : "Salvează"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ModalParola({ angajat, onSalveaza, onInchide, eroare, salveaza }) {
  const [parola, setParola] = useState("");
  const submit = (e) => {
    e.preventDefault();
    onSalveaza(parola);
  };
  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.35)" }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content radius-16 border bg-base">
          <form onSubmit={submit}>
            <div className="modal-header p-24 pb-12">
              <h5 className="modal-title">Parolă nouă — {angajat.nume}</h5>
              <button type="button" className="btn-close" onClick={onInchide} aria-label="Închide" />
            </div>
            <div className="modal-body p-24 pt-12">
              {eroare && <div className="alert alert-danger py-8">{eroare}</div>}
              <label className="form-label fw-semibold text-secondary-light" htmlFor="parola-noua">
                Parolă nouă (minim 8 caractere) — sesiunile existente se închid
              </label>
              <input id="parola-noua" type="password" className="form-control radius-8"
                value={parola} onChange={(e) => setParola(e.target.value)} required minLength={8} />
            </div>
            <div className="modal-footer p-24 pt-0">
              <button type="button" className="btn btn-outline-secondary radius-8" onClick={onInchide}>Anulează</button>
              <button type="submit" className="btn btn-primary radius-8" disabled={salveaza}>
                {salveaza ? "Se salvează…" : "Schimbă parola"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AngajatiPage() {
  usePageTitle("Angajați");
  const { user: eu } = useAuth();
  const [angajati, setAngajati] = useState([]);
  const [roluri, setRoluri] = useState([]);
  const [incarcare, setIncarcare] = useState(true);
  const [eroareLista, setEroareLista] = useState("");

  const [modal, setModal] = useState(null); // null | {editare}
  const [modalParola, setModalParola] = useState(null);
  const [confirm, setConfirm] = useState(null); // {titlu, text, actiune}
  const [salveaza, setSalveaza] = useState(false);
  const [eroareForm, setEroareForm] = useState("");

  const incarca = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([api.get("/api/users"), api.get("/api/users/roles")]);
      setAngajati(u.users);
      setRoluri(r.roles);
      setEroareLista("");
    } catch (err) {
      setEroareLista(err.message);
    } finally {
      setIncarcare(false);
    }
  }, []);

  useEffect(() => {
    incarca();
  }, [incarca]);

  const deschideCreare = () => {
    setEroareForm("");
    setModal({});
  };
  const deschideEditare = (a) => {
    setEroareForm("");
    setModal({ editare: a });
  };

  const salveazaAngajat = async (form) => {
    setSalveaza(true);
    setEroareForm("");
    try {
      if (modal.editare) {
        await api.patch(`/api/users/${modal.editare.id}`, {
          nume: form.nume,
          email: form.email,
          roleId: Number(form.roleId),
        });
      } else {
        await api.post("/api/users", { ...form, roleId: Number(form.roleId) });
      }
      setModal(null);
      incarca();
    } catch (err) {
      setEroareForm(err.message);
    } finally {
      setSalveaza(false);
    }
  };

  const salveazaParola = async (parola) => {
    setSalveaza(true);
    setEroareForm("");
    try {
      await api.post(`/api/users/${modalParola.id}/password`, { password: parola });
      setModalParola(null);
    } catch (err) {
      setEroareForm(err.message);
    } finally {
      setSalveaza(false);
    }
  };

  const executaConfirm = async () => {
    const actiune = confirm.actiune;
    setConfirm(null);
    try {
      await actiune();
      incarca();
    } catch (err) {
      setEroareLista(err.message);
    }
  };

  const cereDezactivare = (a) =>
    setConfirm({
      titlu: "Dezactivezi angajatul?",
      text: `${a.nume} nu se va mai putea autentifica. Poți reactiva oricând.`,
      actiune: () => api.post(`/api/users/${a.id}/dezactivare`),
    });

  const cereActivare = (a) =>
    setConfirm({
      titlu: "Reactivezi angajatul?",
      text: `${a.nume} se va putea autentifica din nou.`,
      actiune: () => api.post(`/api/users/${a.id}/activare`),
    });

  if (incarcare) {
    return (
      <CrmLayout>
        <div className="text-center py-64">
          <div className="spinner-border text-primary-600" role="status">
            <span className="visually-hidden">Se încarcă…</span>
          </div>
        </div>
      </CrmLayout>
    );
  }

  return (
    <CrmLayout>
      <div className="card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h5 className="card-title mb-0">Angajați și roluri</h5>
            <p className="text-secondary-light mb-0">
              {angajati.length} angajați • {angajati.filter((a) => a.activ).length} activi
            </p>
          </div>
          <button type="button" className="btn btn-primary radius-8" onClick={deschideCreare}>
            <Icon icon="fa6-regular:square-plus" className="icon text-lg me-8" />
            Adaugă angajat
          </button>
        </div>
        <div className="card-body p-0">
          {eroareLista && <div className="alert alert-danger mx-24 mt-16">{eroareLista}</div>}
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Nume</th>
                  <th scope="col">Email</th>
                  <th scope="col">Rol</th>
                  <th scope="col">Status</th>
                  <th scope="col">Creat la</th>
                  <th scope="col" className="text-center">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {angajati.map((a) => (
                  <tr key={a.id} className={a.activ ? "" : "opacity-50"}>
                    <td>
                      <div className="d-flex align-items-center gap-12">
                        <span className="w-32-px h-32-px bg-primary-50 text-primary-600 radius-8 d-flex justify-content-center align-items-center fw-semibold">
                          {a.nume.slice(0, 1).toUpperCase()}
                        </span>
                        {a.nume}
                        {a.id === eu?.id && <span className="badge text-sm bg-neutral-200">eu</span>}
                      </div>
                    </td>
                    <td>{a.email}</td>
                    <td>
                      <span className={`badge radius-8 px-12 py-6 ${ROL_BADGE[a.role] || "bg-neutral-100"}`}>
                        {ROL_LABEL[a.role] || a.role}
                      </span>
                    </td>
                    <td>
                      <span className={`badge radius-8 px-12 py-6 ${a.activ ? "bg-success-50 text-success-600" : "bg-danger-50 text-danger-600"}`}>
                        {a.activ ? "Activ" : "Dezactivat"}
                      </span>
                    </td>
                    <td>{fmtData(a.createdAt)}</td>
                    <td>
                      <div className="d-flex justify-content-center gap-8">
                        <button type="button" className="btn btn-sm btn-outline-primary radius-8" title="Editează"
                          onClick={() => deschideEditare({ ...a, roleId: roluri.find((r) => r.nume === a.role)?.id })}>
                          <Icon icon="solar:pen-outline" className="icon" />
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary radius-8" title="Schimbă parola"
                          onClick={() => { setEroareForm(""); setModalParola(a); }}>
                          <Icon icon="solar:lock-password-outline" className="icon" />
                        </button>
                        {a.activ ? (
                          <button type="button" className="btn btn-sm btn-outline-danger radius-8" title="Dezactivează"
                            onClick={() => cereDezactivare(a)}>
                            <Icon icon="solar:user-block-rounded-outline" className="icon" />
                          </button>
                        ) : (
                          <button type="button" className="btn btn-sm btn-outline-success radius-8" title="Reactivează"
                            onClick={() => cereActivare(a)}>
                            <Icon icon="solar:user-check-rounded-outline" className="icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal && (
        <ModalAngajat
          editare={modal.editare}
          roluri={roluri}
          onSalveaza={salveazaAngajat}
          onInchide={() => setModal(null)}
          eroare={eroareForm}
          salveaza={salveaza}
        />
      )}
      {modalParola && (
        <ModalParola
          angajat={modalParola}
          onSalveaza={salveazaParola}
          onInchide={() => setModalParola(null)}
          eroare={eroareForm}
          salveaza={salveaza}
        />
      )}
      {confirm && (
        <ConfirmDialog
          titlu={confirm.titlu}
          text={confirm.text}
          onDa={executaConfirm}
          onNu={() => setConfirm(null)}
        />
      )}
    </CrmLayout>
  );
}
